import React, { useState, useEffect, useCallback } from "react"
import { TimeMachine, UI, useTimeMachine } from "react-time-machine-js"
import { isProduction } from "../../utils/enviroment.utils"
import {
  backendTimePlugin,
  STORAGE_KEY as TIME_STORAGE_KEY,
  setBackendHeader,
  clearBackendHeader,
} from "../../plugins/backendTimePlugin"
import {
  getImpersonationUID,
  setImpersonationUID,
  clearImpersonationUID,
} from "../../plugins/impersonation"
import { useSession } from "../../hooks/auth/useSession.hook"
import "./DebugToolsWidget.css"
import { DISABLE_DEBUG_WIDGET } from "../../consts/enviroments.consts"
import { getUsuarios } from "../../services/usuario.service"

/** Syncs backend time header based on TimeMachine state + reports active state upward */
const BackendTimeSync = ({ onActiveChange }) => {
  const { active, displayTime, store } = useTimeMachine()

  useEffect(() => {
    onActiveChange(active)
  }, [active, onActiveChange])

  useEffect(() => {
    const settings = store.get(TIME_STORAGE_KEY) || {}
    if (active) {
      setBackendHeader(displayTime, settings)
    } else {
      clearBackendHeader()
    }
  }, [active, displayTime, store])

  return null
}

const ImpersonateTab = ({ impersonatingUID, onImpersonationChange }) => {
  const { user, refreshUser } = useSession()
  const [usuarios, setUsuarios] = useState([])
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const fetchUsuarios = async () => {
      setLoading(true)
      const resp = await getUsuarios()
      if (resp.success) {
        setUsuarios(resp.result || [])
      }
      setLoading(false)
    }
    fetchUsuarios()
  }, [])

  const handleImpersonate = async (uid) => {
    setImpersonationUID(uid)
    onImpersonationChange(uid)
    await refreshUser()
  }

  const handleStop = async () => {
    clearImpersonationUID()
    onImpersonationChange(null)
    await refreshUser()
  }

  const filteredUsuarios = usuarios
    .filter((u) => {
      const haystack = [
        u.name,
        u.lastName,
        u.email,
        u.alias,
        u.id != null ? String(u.id) : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
      return haystack.includes(search.toLowerCase())
    })
    .sort((a, b) => {
      const labelA = `${a.name || ""} ${a.lastName || ""}`.trim()
      const labelB = `${b.name || ""} ${b.lastName || ""}`.trim()
      return labelA.localeCompare(labelB, "es", { sensitivity: "base" })
    })

  return (
    <div className="impersonate-container">
      {impersonatingUID && (
        <div className="impersonate-status">
          <strong>Impersonating:</strong>
          <br />
          {user?.nameTag} - {user?.nombre}
          <button className="impersonate-stop-btn" onClick={handleStop}>
            Stop Impersonating
          </button>
        </div>
      )}

      <input
        type="text"
        className="impersonate-search"
        placeholder="Search users..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="impersonate-list">
        {loading && (
          <div style={{ textAlign: "center", padding: "10px" }}>Loading...</div>
        )}
        {filteredUsuarios.map((u) => {
          const tag = u.alias || u.email || (u.id != null ? `#${u.id}` : "")
          const canImpersonate = Boolean(u.uidFirebase)
          return (
            <div
              key={u.id ?? u.uidFirebase}
              className={`impersonate-item ${impersonatingUID === u.uidFirebase ? "active" : ""} ${!canImpersonate ? "impersonate-item--disabled" : ""}`}
              onClick={() =>
                canImpersonate ? handleImpersonate(u.uidFirebase) : undefined
              }
            >
              <span className="impersonate-tag">{tag}</span>
              {u.name} {u.lastName}
            </div>
          )
        })}
        {!loading && filteredUsuarios.length === 0 && (
          <div style={{ textAlign: "center", color: "#666", padding: "10px" }}>
            No users found
          </div>
        )}
      </div>
    </div>
  )
}

const BackendTimePanelRenderer = () => {
  const Panel = backendTimePlugin.panel
  return Panel ? <Panel /> : null
}

export const DebugToolsWidget = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState("time")
  const [timeTravelActive, setTimeTravelActive] = useState(false)
  const [impersonatingUID, setImpersonatingUID] = useState(() =>
    getImpersonationUID()
  )

  const onTimeTravelActiveChange = useCallback((active) => {
    setTimeTravelActive(active)
  }, [])

  const onImpersonationChange = useCallback((uid) => {
    setImpersonatingUID(uid)
  }, [])

  if (isProduction() || DISABLE_DEBUG_WIDGET) return null

  const showTimeTravel = isOpen && activeTab === "time"

  const activeToolCount =
    (timeTravelActive ? 1 : 0) + (impersonatingUID ? 1 : 0)
  const dotColor = activeToolCount > 0 ? "#e74c3c" : "#2ecc71"
  const label =
    activeToolCount > 0 ? `Debug Tools (${activeToolCount})` : "Debug Tools"

  return (
    <div className="debug-tools-widget">
      <button
        className="debug-tools-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span style={{ color: dotColor }}>●</span>
        {label}
      </button>

      {/* TimeMachine is always mounted to prevent auto-cleanup on unmount. */}
      <TimeMachine
        static
        plugins={[backendTimePlugin]}
        onTravel={() => window.location.reload()}
        onReturnToPresent={() => window.location.reload()}
        translations={{
          realTime: "Tiempo real",
          flowing: "Fluido",
          frozen: "Congelado",
          mode: "Modo",
          activate: "Activar",
          returnToPresent: "Volver al presente",
          coreTab: "Principal",
        }}
      >
        <BackendTimeSync onActiveChange={onTimeTravelActiveChange} />
        {isOpen && (
          <div className="debug-tools-panel">
            <div className="debug-tools-tabs">
              <div
                className={`debug-tools-tab ${activeTab === "time" ? "active" : ""}`}
                onClick={() => setActiveTab("time")}
              >
                Time Travel
              </div>
              <div
                className={`debug-tools-tab ${activeTab === "impersonate" ? "active" : ""}`}
                onClick={() => setActiveTab("impersonate")}
              >
                Impersonate
              </div>
            </div>

            <div className="debug-tools-content">
              {showTimeTravel && (
                <>
                  <TimeMachine.StatusBar />
                  <TimeMachine.Panel>
                    <TimeMachine.Input />
                    <TimeMachine.ModeToggle />
                    <div
                      style={{ display: "flex", gap: "8px", marginTop: "12px" }}
                    >
                      <TimeMachine.ActivateButton asChild>
                        <UI.Button variant="primary">Activar</UI.Button>
                      </TimeMachine.ActivateButton>
                      <TimeMachine.ResetButton asChild>
                        <UI.Button variant="danger">Restablecer</UI.Button>
                      </TimeMachine.ResetButton>
                    </div>
                  </TimeMachine.Panel>

                  {/* Backend time plugin panel rendered manually as we are using a custom layout */}
                  <div
                    style={{
                      marginTop: "16px",
                      borderTop: "1px solid #333",
                      paddingTop: "12px",
                    }}
                  >
                    <BackendTimePanelRenderer />
                  </div>
                </>
              )}
              {activeTab === "impersonate" && (
                <ImpersonateTab
                  impersonatingUID={impersonatingUID}
                  onImpersonationChange={onImpersonationChange}
                />
              )}
            </div>
          </div>
        )}
      </TimeMachine>
    </div>
  )
}
